import { SESSION } from "./config";
import { isBrowser } from "./env";

interface SessionInfo {
  id: string;
  started_at: number;
  last_activity: number;
  utc_date: string;
}

function uuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "s-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function read(): SessionInfo | null {
  if (!isBrowser()) return null;
  try {
    const raw = sessionStorage.getItem(SESSION.storage_key);
    return raw ? (JSON.parse(raw) as SessionInfo) : null;
  } catch {
    return null;
  }
}

function write(s: SessionInfo): void {
  if (!isBrowser()) return;
  try {
    sessionStorage.setItem(SESSION.storage_key, JSON.stringify(s));
  } catch {
    /* noop */
  }
}

export type SessionEndedReason = "idle" | "day_boundary" | "visibility_hidden" | "explicit";

export interface SessionTransition {
  ended: { id: string; duration_ms: number; reason: SessionEndedReason } | null;
  current: SessionInfo;
}

export function touchSession(): SessionTransition {
  const now = Date.now();
  const today = todayUtc();
  const prev = read();

  if (prev) {
    const idle = now - prev.last_activity > SESSION.ttl_ms;
    const dayChanged = prev.utc_date !== today;
    if (!idle && !dayChanged) {
      const updated = { ...prev, last_activity: now };
      write(updated);
      return { ended: null, current: updated };
    }
    const fresh: SessionInfo = { id: uuid(), started_at: now, last_activity: now, utc_date: today };
    write(fresh);
    return {
      ended: { id: prev.id, duration_ms: prev.last_activity - prev.started_at, reason: idle ? "idle" : "day_boundary" },
      current: fresh,
    };
  }

  const fresh: SessionInfo = { id: uuid(), started_at: now, last_activity: now, utc_date: today };
  write(fresh);
  return { ended: null, current: fresh };
}

export function getCurrentSession(): SessionInfo {
  return touchSession().current;
}

export function endSessionExplicit(reason: SessionEndedReason): { id: string; duration_ms: number } | null {
  const prev = read();
  if (!prev) return null;
  if (isBrowser()) {
    try {
      sessionStorage.removeItem(SESSION.storage_key);
    } catch {
      /* noop */
    }
  }
  void reason;
  return { id: prev.id, duration_ms: Date.now() - prev.started_at };
}
